import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { Instance } from '../api/client'
import StatusBadge from './StatusBadge'
import LabelPicker from './LabelPicker'
import { useLifecycle } from '../hooks/useInstances'
import { isBusy } from '../lib/instance-status'

export default function InstanceCard({ instance, showOwner, showLabels, onManageLabels }: { instance: Instance; showOwner?: boolean; showLabels?: boolean; onManageLabels?: () => void }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { start, stop, retry } = useLifecycle()

  const busy = isBusy(instance.status)

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 hover:border-brand-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={() => navigate(`/instances/${instance.id}`)}
      style={showLabels && instance.label ? { borderLeftColor: instance.label.color, borderLeftWidth: 3 } : undefined}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{instance.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{instance.subdomain_url}</p>
          {showOwner && instance.created_by_email && (
            <p className="text-xs text-brand-500 mt-0.5">{instance.created_by_email}</p>
          )}
        </div>
        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
          v{instance.odoo_version}
        </span>
      </div>

      {showLabels && (
        <div className="mb-3">
          <LabelPicker instance={instance} onManage={onManageLabels} />
        </div>
      )}

      <StatusBadge status={instance.status} />

      {instance.status_message && busy && (
        <p className="text-xs text-gray-500 mt-2 truncate">
          {t(`status.msg.${instance.status_message}`, instance.status_message)}
        </p>
      )}

      {busy && (
        <div className="mt-3 h-1.5 w-full rounded-full bg-amber-100 overflow-hidden">
          <div className="h-full w-1/2 rounded-full bg-amber-500 animate-pulse" />
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
        {instance.status === 'running' && (
          <>
            {busy ? (
              <span className="text-xs text-gray-400 font-medium" title={t('instanceCard.openUnavailable')}>
                {t('instanceCard.open')}
              </span>
            ) : (
              <a
                href={instance.custom_url || instance.subdomain_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand-600 hover:text-brand-700 font-medium"
              >
                {t('instanceCard.open')}
              </a>
            )}
            <button
              onClick={() => stop.mutate(instance.id)}
              disabled={stop.isPending || busy}
              className="ml-auto text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              {t('instanceCard.stop')}
            </button>
          </>
        )}
        {instance.status === 'stopped' && (
          <button
            onClick={() => start.mutate(instance.id)}
            disabled={start.isPending}
            className="ml-auto text-xs text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
          >
            {t('instanceCard.start')}
          </button>
        )}
        {instance.status === 'error' && (
          <div className="flex items-center gap-2 w-full">
            <span className="text-xs text-red-500 truncate flex-1">{instance.status_message}</span>
            <button
              onClick={() => retry.mutate(instance.id)}
              disabled={retry.isPending}
              className="text-xs text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50 shrink-0"
            >
              {t('instanceCard.retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
